import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export type BadgeTone =
  | 'neutral'
  | 'ok'
  | 'warn'
  | 'stop'
  | 'info'
  | 'leaf'
  | 'clay'
  | 'ink';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-[var(--pv-card-alt)] text-[var(--pv-ink-soft)] border-[var(--pv-line-strong)]',
  ok: 'bg-[var(--pv-ok-bg)] text-[var(--pv-ok)] border-[var(--pv-ok-line)]',
  warn: 'bg-[var(--pv-warn-bg)] text-[var(--pv-warn)] border-[var(--pv-warn-line)]',
  stop: 'bg-[var(--pv-stop-bg)] text-[var(--pv-stop)] border-[var(--pv-stop-line)]',
  info: 'bg-[var(--pv-info-bg)] text-[var(--pv-info)] border-[var(--pv-info-line)]',
  leaf: 'bg-[var(--pv-leaf-soft)] text-[var(--pv-leaf-ink)] border-[var(--pv-leaf)]',
  clay: 'bg-[var(--pv-clay-soft)] text-[var(--pv-clay-deep)] border-[var(--pv-clay)]',
  ink: 'bg-[var(--pv-ink)] text-[var(--pv-on-ink)] border-[var(--pv-ink)]',
};

export function Badge({
  tone = 'neutral',
  children,
  dot = false,
  className,
  size = 'md',
}: {
  tone?: BadgeTone;
  children: ReactNode;
  /** A leading dot in the current colour — reads as a status light. */
  dot?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[var(--pv-radius-pill)] border font-semibold whitespace-nowrap',
        size === 'sm'
          ? 'px-2 py-px [font-size:var(--pv-text-2xs)]'
          : 'px-2.5 py-0.5 [font-size:var(--pv-text-xs)]',
        TONES[tone],
        className,
      )}
    >
      {dot && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/**
 * A code chip — HS codes, certificate numbers, API key ids, document type codes.
 * Monospace with tabular figures so a transposed digit is visible at a glance, which is
 * the whole reason these are set in mono rather than in the UI face.
 */
export function CodeChip({
  children,
  className,
  tone = 'neutral',
}: {
  children: ReactNode;
  className?: string;
  tone?: 'neutral' | 'leaf';
}) {
  return (
    <span
      className={cn(
        'pv-mono inline-flex items-center rounded-[var(--pv-radius-xs)] border px-1.5 py-px [font-size:var(--pv-text-2xs)] tracking-[0.02em]',
        tone === 'leaf'
          ? 'border-[var(--pv-leaf)] bg-[var(--pv-leaf-soft)] text-[var(--pv-leaf-ink)]'
          : 'border-[var(--pv-line-strong)] bg-[var(--pv-card-alt)] text-[var(--pv-ink-soft)]',
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * SLA ageing indicator. Overdue is the state the operator's whole day is organised
 * around, so it is the loudest thing this component can render.
 */
export function AgeingBadge({ daysToTarget }: { daysToTarget: number | null }) {
  if (daysToTarget === null) {
    return (
      <span className="[font-size:var(--pv-text-xs)] text-[var(--pv-faint)]">No target</span>
    );
  }
  if (daysToTarget < 0) {
    const n = Math.abs(daysToTarget);
    return (
      <Badge tone="stop" dot size="sm">
        {n} {n === 1 ? 'day' : 'days'} overdue
      </Badge>
    );
  }
  if (daysToTarget === 0) {
    return (
      <Badge tone="warn" dot size="sm">
        Due today
      </Badge>
    );
  }
  if (daysToTarget <= 2) {
    return (
      <Badge tone="clay" size="sm">
        Due in {daysToTarget}d
      </Badge>
    );
  }
  return (
    <span className="pv-num [font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
      {daysToTarget}d
    </span>
  );
}
